// Thoughts feed (MUI, /feed-bus). Presentation port of FeedPage — same data
// (browseRecent), rebuilt with mui/* primitives + brainTokens under MuiAppShell.
import { useState, useEffect, useCallback, useRef } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import ReactMarkdown from "react-markdown";
import { browseRecent } from "../../lib/brain-api";
import type { Thought } from "../../lib/brain-types";
import { typeColor } from "../../lib/type-colors";
import { brainTokens } from "../../design/brainTheme";
import { PageHeader, Segmented, SectionCard, EmptyState, Icon } from "../../mui";

const c = brainTokens.color;
const SHARED_BORDER = "#0891b2";

type Segment = "private" | "shared";

function relativeTime(ts: number | null): string {
  if (!ts) return "";
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

function FeedCard({ thought, scope }: { thought: Thought; scope: Segment }) {
  const [expanded, setExpanded] = useState(false);
  const color = typeColor(thought.type);
  const borderColor = scope === "private" ? c.teal : SHARED_BORDER;
  const content = thought.content;
  const isLong = content.length > 200;

  return (
    <SectionCard padding={0} sx={{ overflow: "hidden", borderLeft: `2px solid ${borderColor}` }}>
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            {scope === "shared" && (
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  bgcolor: c.panelBg2,
                }}
              >
                <Icon name="user" size={14} style={{ color: c.muted }} />
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color,
                }}
              >
                {thought.type.replace("_", " ")}
              </Box>
              <Box sx={{ fontSize: 10, mt: "2px", color: c.muted }}>
                {relativeTime(thought.created_at)}
                {scope === "shared" && thought.scope === "shared" && (
                  <Box component="span" sx={{ ml: "4px", color: c.tealInk }}>
                    · shared
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          className="kt-markdown"
          sx={{
            fontSize: 14,
            color: c.ink3,
            ...(!expanded && isLong
              ? { display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, overflow: "hidden" }
              : {}),
          }}
        >
          <ReactMarkdown>{expanded || !isLong ? content : content.slice(0, 200) + "…"}</ReactMarkdown>
        </Box>
        {isLong && (
          <Button
            size="small"
            onClick={() => setExpanded((e) => !e)}
            sx={{
              alignSelf: "flex-start",
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: c.muted,
              minWidth: 0,
              p: 0,
            }}
          >
            {expanded ? "Show less ↑" : "Show more ↓"}
          </Button>
        )}

        {(thought.topics.length > 0 || thought.people.length > 0) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {thought.topics.map((t) => (
              <Box
                key={t}
                component="span"
                sx={{ fontSize: 10, px: 1, py: "2px", borderRadius: `${brainTokens.radius.pill}px`, bgcolor: c.tealTint, color: c.tealInk }}
              >
                {t}
              </Box>
            ))}
            {thought.people.map((p) => (
              <Box
                key={p}
                component="span"
                sx={{ fontSize: 10, px: 1, py: "2px", borderRadius: `${brainTokens.radius.pill}px`, bgcolor: c.tealTint, color: c.tealInk }}
              >
                @{p}
              </Box>
            ))}
          </Box>
        )}

        {thought.media_url && (
          <Box sx={{ borderRadius: `${brainTokens.radius.button}px`, overflow: "hidden" }}>
            <Box
              component="img"
              src={thought.media_url}
              alt=""
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              sx={{ width: "100%", maxHeight: 192, objectFit: "cover", borderRadius: `${brainTokens.radius.button}px` }}
            />
          </Box>
        )}
      </Box>
    </SectionCard>
  );
}

export function FeedPageMui() {
  const [segment, setSegment] = useState<Segment>("private");
  const [thoughts, setThoughts] = useState<Thought[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const loadFeed = useCallback(async (scope: Segment) => {
    const id = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const results = await browseRecent({ scope, limit: 50 });
      if (id !== requestIdRef.current) return;
      setThoughts(results);
    } catch (e: unknown) {
      if (id !== requestIdRef.current) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (id === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    setThoughts([]);
    loadFeed(segment);
  }, [segment, loadFeed]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      loadFeed(segment);
    }, 30_000);
    return () => clearInterval(interval);
  }, [segment, loadFeed]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2 }}>
      <PageHeader
        title="Thoughts"
        actions={
          <Button
            onClick={() => loadFeed(segment)}
            disabled={loading}
            aria-label="Refresh"
            title="Refresh"
            aria-busy={loading}
            sx={{ minWidth: 0, p: 1, color: c.muted, opacity: loading ? 0.4 : 1 }}
          >
            {loading ? <CircularProgress size={16} aria-hidden="true" /> : <Icon name="refresh" size={20} />}
          </Button>
        }
      />

      <Segmented
        value={segment}
        onChange={setSegment}
        options={[
          { key: "private", label: "My brain" },
          { key: "shared", label: "Global feed" },
        ]}
      />

      {error && <Box sx={{ fontSize: 13, color: c.dangerText }}>{error}</Box>}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {loading && thoughts.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && thoughts.length === 0 && !error && (
          <EmptyState
            icon={segment === "private" ? "bulb" : "world"}
            title={segment === "private" ? "No thoughts captured yet." : "No shared thoughts yet."}
          />
        )}

        {thoughts.map((t, i) => (
          <FeedCard key={t.id ?? String(t.created_at ?? i)} thought={t} scope={segment} />
        ))}
      </Box>
    </Box>
  );
}
